import type { LicenseInfo } from '#layers/base/schema/license-status';
import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import * as z from 'zod/v4-mini';
import { useSystemFetch } from '#layers/base/composables/use-system-fetch';
import { LicenseStatusSchema } from '#layers/base/schema/license-status';
import { singleFlight } from '#layers/base/utils/single-flight';

const OnboardingInfoSchema = z.object({
	requireCreateUser: z.boolean(),
	requireAcceptTerms: z.boolean(),
});

const AuthenticatedSystemInfoSchema = z.object({
	maxFileUploadSize: z.number(),
	version: z.string(),
	revision: z.string(),
});

const SystemInfoLicenseSchema = z.object({
	status: LicenseStatusSchema,
	expiresAt: z.nullable(z.number()),
	renewsAt: z.nullable(z.number()),
	gracePeriod: z.number(),
});

const SystemInfoResult = z.object({
	onboarding: z.optional(OnboardingInfoSchema),
	authenticated: z.optional(AuthenticatedSystemInfoSchema),
	license: z.optional(SystemInfoLicenseSchema),
	now: z.optional(z.number()),
});

type OnboardingInfo = z.infer<typeof OnboardingInfoSchema>;
type AuthenticatedSystemInfo = z.infer<typeof AuthenticatedSystemInfoSchema>;
export type SystemInfoLicense = z.infer<typeof SystemInfoLicenseSchema>;

const NO_LICENSE_INFO: LicenseInfo = {
	status: null,
	expiresAt: null,
	renewsAt: null,
	gracePeriod: null,
};

export const useSystemInfo = defineStore('system-info', () => {
	const fetch = useSystemFetch();

	/**
	 * Onboarding requirements returned by `/system/info`. `null` means either
	 * the probe hasn't run yet, it failed, or the server reported no
	 * requirements — all three collapse to "no onboarding required".
	 */
	const onboarding = ref<OnboardingInfo | null>(null);
	const authenticated = ref<AuthenticatedSystemInfo | null>(null);
	const license = ref<LicenseInfo>(NO_LICENSE_INFO);
	const serverTimeOffsetMs = ref(0);
	const isInitialized = ref(false);
	const hasReprobedForAuth = ref(false);

	const refresh = singleFlight(async () => {
		try {
			await runRefresh();
		}
		finally {
			isInitialized.value = true;
		}
	});

	async function runRefresh() {
		try {
			const result = SystemInfoResult.parse(await fetch('system/info'));
			onboarding.value = result.onboarding ?? null;
			authenticated.value = result.authenticated ?? null;

			license.value = result.license ?? NO_LICENSE_INFO;

			serverTimeOffsetMs.value = result.now != null
				? result.now * 1000 - Date.now()
				: 0;
		}
		catch (error) {
			console.warn('[useSystemInfo] /system/info probe failed; keeping last known state', error);
		}
	}

	async function ensureLoaded() {
		if (isInitialized.value) {
			return;
		}

		await refresh();
	}

	/**
	 * For callers that have resolved the session as logged in. The boot probe races
	 * `POST /auth/refresh`, so it can go out with an expired access-token cookie —
	 * and the engine answers 200 with the `authenticated` section omitted rather
	 * than an error, which would otherwise latch it away for the whole session.
	 *
	 * One-shot: a server that keeps omitting the section must not cost a request on
	 * every navigation.
	 */
	async function ensureAuthenticatedLoaded() {
		if (authenticated.value !== null || hasReprobedForAuth.value) {
			return;
		}

		await refresh();
		hasReprobedForAuth.value = true;
	}

	/** Ends the probe epoch: the next authenticated caller gets a fresh attempt. */
	function resetAuthProbe() {
		hasReprobedForAuth.value = false;
	}

	return {
		refresh,
		ensureLoaded,
		ensureAuthenticatedLoaded,
		resetAuthProbe,
		onboarding: computed(() => onboarding.value),
		maxFileUploadSize: computed(() => authenticated.value?.maxFileUploadSize ?? null),
		license: computed(() => readonly(license.value)),
		serverTimeOffsetMs: computed(() => serverTimeOffsetMs.value),
		version: computed(() => authenticated.value?.version ?? null),
		revision: computed(() => authenticated.value?.revision ?? null),
	};
});
